import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import HubCountryLinks from "@/components/HubCountryLinks";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";
import { canonicalAndHreflangEn, ctaDesc, ctaTitle } from "@/lib/seo";
import { SWORN_LANGUAGES } from "@/data/sworn-languages";

const TITLE = "Sworn Translator Thailand — 20+ Court-Sworn Languages | NYC";
const DESC = "Court-sworn translators registered with foreign Ministries of Justice / Courts (German §189 GVG. Get your timeline and checklist in minutes — chat now.";

const steps = [
  { name: "Destination check", text: "Tell us the country/authority; we pick the correct sworn class (§189 GVG DE / Cour d'appel FR / MAEC ES / Thai MoJ)." },
  { name: "Submit source (300dpi)", text: "Send via LINE / email — quote & timeline within 30 minutes." },
  { name: "Sworn translation", text: "By translators registered under the destination law, with verifiable registry numbers." },
  { name: "4-eyes QA + sworn stamp", text: "Second-linguist review + sworn seal + Certificate of Accuracy." },
  { name: "MFA + Apostille (if needed)", text: "Chain into Thai MFA + Apostille (Hague, in force 15 Dec 2025) or Embassy Legalization." },
  { name: "Worldwide delivery", text: "Free EMS in Thailand · DHL/FedEx 2–5 business days globally." },
];

const faqs = [
  { q: "How is Sworn different from Certified translation?", a: "Sworn translators are appointed by a court / state authority in the destination country (e.g. beeidigter Übersetzer in Germany, Traducteur assermenté in France). Their translation is a legal instrument on its own — no separate notary needed. Certified translations only attest translator identity." },
  { q: "Which countries require sworn translators?", a: "Germany, France, Spain, Italy, Netherlands, Poland, Portugal, Brazil, Argentina, plus Indonesia (Penerjemah Tersumpah). The UK, USA, Australia, Canada, Japan, and Korea use certified / NAATI / MoJ instead." },
  { q: "Pricing?", a: "English ฿1,500/page; Chinese, Japanese, Korean ฿1,800–2,000; German, French, Spanish, Italian ฿2,400; Arabic, Russian, Dutch, Polish ฿2,600–2,800 per A4 page. Every price includes the sworn translator's official seal, signature, and a signed Certificate of Accuracy on NYC letterhead — no hidden fees for QA or delivery within Thailand." },
  { q: "Turnaround?", a: "Major world languages (EN, CN, JP, KR) 1–3 business days; core EU languages (DE, FR, ES, IT, NL) 3–5 days; rare pairs (AR, RU, PL, PT, ID) 5–7 days. Express 24-hour service adds 50% and is capped at 15 pages per file to maintain 4-eyes QA. All quotes confirmed within 30 minutes via LINE." },
  { q: "Do I also need MFA + Apostille?", a: "Depends on destination. Translations done abroad by a local sworn translator often skip Thai MFA; documents translated in Thailand for EU use require Thai MFA + Apostille first." },
  { q: "Which document types?", a: "Court judgments, contracts, powers of attorney, wills, corporate affidavits, audited financials, university transcripts, diplomas, marriage/divorce certificates, birth/death certificates, medical reports, patent specifications, and adoption papers — all handled with matching subject-matter specialists per language pair." },
  { q: "How can I verify the translator?", a: "Every sworn translator we assign is listed in the destination country's public registry: Germany justiz-dolmetscher.de, France annuaire.cour-de-cassation.fr, Spain exteriores.gob.es (MAEC), Italy Tribunale CTU list, and Thailand coj.go.th. We include the registry number on the Certificate of Accuracy so any receiving authority can cross-check independently." },
  { q: "Do you deliver worldwide?", a: "Yes — active sworn-translator network in Berlin, Munich, Frankfurt, Paris, Lyon, Madrid, Barcelona, Rome, Milan, Amsterdam, Warsaw, and Lisbon. Hardcopies ship via DHL Express with tracking to 220+ countries in 2–5 business days; digitally-signed PDFs are emailed same day at no extra cost." },
];

export const Route = createFileRoute("/en/sworn-translator/")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/sworn-translator", "en")}, { name: "description", content: ctaDesc(DESC, "/en/sworn-translator", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/sworn-translator", "en")}, { property: "og:description", content: ctaDesc(DESC, "/en/sworn-translator", "en")},
      { property: "og:url", content: "https://nyclanguageinstitute.com/en/sworn-translator" },
    ],
    links: canonicalAndHreflangEn("/en/sworn-translator", "/sworn-translator"),
    scripts: buildLandingSchemas({
      path: "/en/sworn-translator", title: TITLE, description: DESC,
      serviceType: "Sworn Translation Service", priceFromThb: 1500, steps, faqs,
      breadcrumbs: [{ name: "Home", path: "/en" }, { name: "Services", path: "/en/services" }, { name: "Sworn Translator", path: "/en/sworn-translator" }],
    }),
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <ServiceLanding
        badge="Court-sworn · 20+ languages"
        title="Sworn Translator Thailand — 20+ languages"
        subtitle={DESC}
        quickAnswer={{
          q: "What is a Sworn Translator and how does it differ from Certified?",
          a: "Court-appointed translators (beeidigter Übersetzer, Traducteur assermenté, Traductor jurado, etc.) whose translation is legally valid at destination without a separate notary. NYC covers 20+ languages from ฿1,500/page with optional MFA + Apostille bundle.",
        }}
        heroBullets={[
          "20+ languages (EN/CN/JP/KR/DE/FR/ES/IT/NL/PL/AR/RU/HI/VI/ID …)",
          "Registered in destination-country registries — verifiable numbers",
          "4-eyes QA under ISO 17100:2015+A1:2024",
          "One-stop MFA + Apostille + Embassy bundle",
        ]}
        whoFor={{
          th: ["Applicants filing with EU/LATAM courts or authorities", "Credential-evaluation candidates in DE/FR/ES/IT", "Cross-border contract signatories", "Spouses registering marriage in the EU"],
          foreign: ["Foreigners in Thailand needing sworn translations for home country", "Law firms handling cross-border litigation"],
        }}
        steps={steps}
        pricing={SWORN_LANGUAGES.slice(0, 10).map((l) => ({ label: `${l.en}`, price: `${l.priceFrom.toLocaleString()}/page`, note: l.turnaround }))}
        faqs={faqs}
        relatedLinks={[
          { to: "/en/moj-translator", label: "MoJ Sworn Translator" },
          { to: "/en/translate", label: "Certified Translation" },
          { to: "/en/notary", label: "Notary Public" },
          { to: "/en/apostille", label: "Apostille (Hague)" },
          { to: "/en/embassy", label: "Embassy Legalization" },
          { to: "/en/services", label: "All services" },
        ]}
      />
      <section className="mx-auto max-w-5xl px-4 py-12">
        <h2 className="text-2xl font-bold">Pick your sworn language</h2>
        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {SWORN_LANGUAGES.map((l) => (
            <Link key={l.slug} to="/en/sworn-translator/$language" params={{ language: l.slug }}
              className="rounded border p-3 hover:border-primary">
              <div className="font-semibold">{l.en}</div>
              <div className="text-xs text-muted-foreground">From ฿{l.priceFrom.toLocaleString()}/page · {l.turnaround}</div>
              <div className="text-xs mt-1 text-primary">{l.registryEn}</div>
            </Link>
          ))}
        </div>
        <div className="mt-8 rounded-lg border border-border p-4">
          <h2 className="text-xl font-semibold">Guide to documents that need certified translation</h2>
          <p className="mt-2 text-sm leading-7 text-muted-foreground">
            Six document families (litigation, civil registry, education, corporate, immigration,
            medical/insurance) with the certification order, preparation checklist and FAQs for each.
          </p>
          <Link to="/en/sworn-translator/documents" className="mt-3 inline-block text-primary hover:underline">
            Browse all six document families →
          </Link>
        </div>
      </section>
      <HubCountryLinks service="apostille" lang="en" />
    </>
  );
}
